import { navigate, RouteComponentProps } from '@reach/router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import css from './RegisterEquipmentForm.module.scss';
import { Input } from '../Input/Input';
import { escapeHtml, logger } from '../../utils';
import { Button } from '../Button/Button';
import { api } from '../../state/effects';
import { EmailStatus, EmailType, RegisterRequest } from '../../../interfaces/customer-api';
import { useOvermindState } from '../../state';
import { Spinner } from '../Spinner/Spinner';
import { CompleteRequestDialog } from '../CompleteRequestDialog/CompleteRequestDialog';

const emailBodyHtmlBuilder = (
    userName: string,
    customerNumber: string,
    phoneNumber: string,
    email: string,
    description: string,
): string => {
    const htmlTemplate = `
       <p>A equipment register request was created by customer:</p>
       <p>Username: ${escapeHtml(userName)}</p>
       <p><b>Customer number:</b> ${escapeHtml(customerNumber)}</p>
       <p>Phone number: ${escapeHtml(phoneNumber)}</p>
       <p>Email: ${escapeHtml(email)}
       <p>Description: ${escapeHtml(description)}</p>
    `;
    // Remove tabs from beginning of each row, so DB won't show unnecessary tabs
    return htmlTemplate.replace(/^\s*\n|\n\s*$/g, '').replace(/^\s*/gm, '');
};

export const RegisterEquipmentForm: React.FC<RouteComponentProps> = () => {
    // FIX ME: Create a own namespace for this component
    const { t } = useTranslation('EmptyWidgets');
    const { user, selectedCustomerNumber, selectedCustomer } = useOvermindState();
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState(user?.email || '');
    const [description, setDescription] = useState('');
    const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Reset the error dialog value
        setShowErrorDialog(false);
        setIsButtonDisabled(true);
        setShowSpinner(true);

        if (selectedCustomerNumber && user && user.id && user.email) {
            try {
                const request = await api.insertEmail(selectedCustomerNumber, {
                    userId: user.id,
                    customerNumber: selectedCustomerNumber,
                    userName: user.name || '',
                    customerName: selectedCustomer?.name || '',
                    senderEmail: user.email,
                    // sentToEmail: string[],
                    emailSubject: `Equipment register request form ${selectedCustomerNumber}`,
                    emailBodyJson: {
                        description: description,
                    } as RegisterRequest,
                    emailBodyHtml: emailBodyHtmlBuilder(
                        user.name || '',
                        selectedCustomerNumber,
                        phoneNumber,
                        email,
                        description,
                    ),
                    type: EmailType.RequestRegistration,
                    status: EmailStatus.Created,
                    createdOn: Date.now().toString(),
                });
                if (request !== null) {
                    setIsCompleteDialogOpen(true);
                } else {
                    setShowErrorDialog(true);
                    setIsCompleteDialogOpen(true);
                }
            } catch (error) {
                setShowErrorDialog(true);
                setIsCompleteDialogOpen(true);
                logger.error('Error sending service request:', error);
            }
        } else {
            logger.info('No customer selected');
        }

        setShowSpinner(false);
        setIsButtonDisabled(false);
    };

    // Check if all required fields are filled
    useEffect(() => {
        const allFilled = [phoneNumber, email, description].every((val) => val.toString().trim() !== '');
        setIsButtonDisabled(!allFilled);
    }, [phoneNumber, email, description]);

    return (
        <>
            <form id="equipment-register-request-form" onSubmit={handleSubmit}>
                <div className={css.formContainer}>
                    <div className={css.formHead}>{t('Details')}</div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Customer number')}
                        <Input
                            inputProps={{
                                value: selectedCustomerNumber || '',
                                disabled: true,
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('User')}
                        <Input
                            inputProps={{
                                value: user?.name,
                                disabled: true,
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Phone number')}
                        <Input
                            inputProps={{
                                value: phoneNumber,
                                onChange: ({ target }) => {
                                    // Allow only + sign and numbers
                                    const value = target.value.replace(/[^+0-9]/g, '');
                                    setPhoneNumber(value);
                                },
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Email')}
                        <Input
                            inputProps={{
                                value: email,
                                onChange: ({ target }) => setEmail(target.value),
                            }}
                        />
                    </div>
                    <div className={css.description}>
                        <span className={css.star}>*</span>
                        {t('Description')}
                        <Input
                            multiline
                            textareaProps={{
                                value: description,
                                onChange: ({ target }) => setDescription(target.value),
                                placeholder: t(
                                    'Description (serial numbers of all the equipment you want to register)',
                                ),
                                rows: 4,
                            }}
                        />
                    </div>
                    <div className={css.buttonContainer}>
                        <Button
                            type="button"
                            variant="primary"
                            className={css.button}
                            onClick={() => navigate(-1)} /* Navigate back */
                        >
                            {t('Cancel')}
                        </Button>
                        <Button type="submit" variant="primary" className={css.button} isDisabled={isButtonDisabled}>
                            {showSpinner ? (
                                <div className={css.spinner}>
                                    <Spinner size={2} />
                                </div>
                            ) : (
                                t('Send')
                            )}
                        </Button>
                    </div>
                </div>
            </form>
            {isCompleteDialogOpen && (
                <CompleteRequestDialog
                    setIsCompleteDialogOpen={setIsCompleteDialogOpen}
                    isErrorDialog={showErrorDialog}
                    headerText={showErrorDialog ? t('Request submission failed') : t('Registration request received')}
                    successText={t(
                        'Thank you for your registration request. We will handle your request as soon as possible.',
                    )}
                    errorText={t('Something went wrong')}
                    closeButtonText={t('Close')}
                    onClose={() => {
                        if (!showErrorDialog) navigate(-1);
                    }}
                />
            )}
        </>
    );
};
